// ── On/off and phrase rows for settings ─────────────────────────────────────
// One switch for every boolean in Settings (not a checkbox here, a pill there).
// The facts about a row, set apart by space, are components/Phrases.tsx.
import type { ReactNode } from 'react'
import { cx } from '@/components/ui'

/** A switch: the track and knob only. `label` names it for a screen reader. */
export const Switch = ({
  on,
  onChange,
  label,
  disabled,
  testId,
}: {
  on: boolean
  onChange: (on: boolean) => void
  label: string
  disabled?: boolean
  testId?: string
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    data-testid={testId}
    className={cx(
      'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors duration-150 disabled:opacity-40',
      on ? 'bg-accent border-accent' : 'bg-raised border-line-strong',
    )}
  >
    <span
      aria-hidden
      className={cx(
        'absolute h-5 w-5 rounded-full transition-transform duration-150',
        on ? 'translate-x-[22px] bg-on-accent' : 'translate-x-[3px] bg-muted',
      )}
    />
  </button>
)

/** A whole-row switch: the words on the left, the switch on the right, a hint under them. */
export const SwitchRow = ({
  label,
  hint,
  on,
  onChange,
  disabled,
  testId,
  className,
}: {
  label: string
  hint?: ReactNode
  on: boolean
  onChange: (on: boolean) => void
  disabled?: boolean
  testId?: string
  className?: string
}) => (
  <div
    className={cx(
      'flex items-center gap-3 min-h-12 py-1.5',
      disabled ? 'opacity-60' : 'cursor-pointer',
      className,
    )}
    onClick={(e) => {
      // the switch answers its own click; anywhere else on the row flips it too
      if (disabled || (e.target as HTMLElement).closest('[role="switch"]')) return
      onChange(!on)
    }}
  >
    <span className="min-w-0 flex-1">
      <span className="block text-[15px] leading-5 text-fg">{label}</span>
      {hint && <span className="block text-xs text-faint mt-0.5">{hint}</span>}
    </span>
    <Switch on={on} onChange={onChange} label={label} disabled={disabled} testId={testId} />
  </div>
)
